import api from '@/utils/axios';
import { unwrapItem, unwrapList } from '@/utils/apiHelpers';

export interface GeoItem {
  id: number;
  name: string;
  code?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  is_active?: boolean;
  country_id?: number;
  region_id?: number;
  prefecture_id?: number;
  commune_id?: number;
  district_id?: number;
}

export interface GeoHierarchy {
  country: GeoItem | null;
  region: GeoItem | null;
  prefecture: GeoItem | null;
  commune: GeoItem | null;
  district: GeoItem | null;
  secteur: GeoItem;
}

export async function fetchCountries(): Promise<{ countries: GeoItem[]; defaultCountry: GeoItem | null }> {
  const res = await api.get('/geography/countries');
  return {
    countries: unwrapList<GeoItem>(res.data),
    defaultCountry: (res.data.default_country ?? null) as GeoItem | null,
  };
}

export async function fetchRegions(countryId: number): Promise<GeoItem[]> {
  const res = await api.get(`/geography/countries/${countryId}/regions`);
  return unwrapList<GeoItem>(res.data);
}

export async function fetchPrefectures(regionId: number): Promise<GeoItem[]> {
  const res = await api.get(`/geography/regions/${regionId}/prefectures`);
  return unwrapList<GeoItem>(res.data);
}

export async function fetchCommunes(prefectureId: number): Promise<GeoItem[]> {
  const res = await api.get(`/geography/prefectures/${prefectureId}/communes`);
  return unwrapList<GeoItem>(res.data);
}

export async function fetchDistricts(communeId: number): Promise<GeoItem[]> {
  const res = await api.get(`/geography/communes/${communeId}/districts`);
  return unwrapList<GeoItem>(res.data);
}

export async function fetchSecteurs(districtId: number): Promise<GeoItem[]> {
  const res = await api.get(`/geography/districts/${districtId}/secteurs`);
  return unwrapList<GeoItem>(res.data);
}

export async function resolveSecteur(secteurId: number): Promise<GeoHierarchy> {
  const res = await api.get(`/geography/secteurs/${secteurId}/resolve`);
  return unwrapItem<GeoHierarchy>(res.data);
}

export async function createGeoEntity(
  level: 'countries' | 'regions' | 'prefectures' | 'communes' | 'districts' | 'secteurs',
  payload: Record<string, unknown>,
): Promise<GeoItem> {
  const res = await api.post(`/geography/admin/${level}`, payload);
  return unwrapItem<GeoItem>(res.data);
}

export async function updateGeoEntity(
  level: 'countries' | 'regions' | 'prefectures' | 'communes' | 'districts' | 'secteurs',
  id: number,
  payload: Record<string, unknown>,
): Promise<GeoItem> {
  const res = await api.patch(`/geography/admin/${level}/${id}`, payload);
  return unwrapItem<GeoItem>(res.data);
}

export async function deleteGeoEntity(
  level: 'countries' | 'regions' | 'prefectures' | 'communes' | 'districts' | 'secteurs',
  id: number,
): Promise<void> {
  await api.delete(`/geography/admin/${level}/${id}`);
}
